<template lang="pug">
    el-upload.attachment-upload(
        :class="{ 'plus-box': dropzone }"
        action="#"
        :drag="dropzone"
        :disabled="disabled"
        :show-file-list="false"
        :multiple="multiple"
        :http-request="upload"
    )
        //- The admin's one dropzone: the .plus-box tile every other upload
        //- field uses (product media, member documents, the CSV imports).
        template(v-if="dropzone")
            i.el-icon-plus
            .el-upload__text(v-html="$t('upload.drop.plural')")
            //- Room for a second source inside the tile — the media library,
            //- the way the product media dropzone carries it.
            slot
        el-button(v-else plain size="mini" :disabled="disabled" :icon="icon") {{ label || $t('Attach') }}
</template>

<i18n>
{
    "de": {
        "Attach": "Anhang",
        "Upload failed": "Upload fehlgeschlagen"
    }
}
</i18n>

<script>
import { useSetupStore } from '@/stores'
import AuthApi from '@/store/api/system/auth'
import { ensureFreshToken } from '@/helpers'

export default {
    name: 'AttachmentUpload',
    props: {
        // Render as a full drag-and-drop zone (used in the compose drawer); the
        // default is the compact inline "Anhang" button (used in the reply bar).
        dropzone: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        // WhatsApp allows only one file per message; email allows several.
        multiple: {
            type: Boolean,
            default: true
        },
        // Outside a message composer "Anhang" is the wrong word — it reads as
        // an email attachment even when the file belongs to the record itself.
        label: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: 'el-icon-fa-paperclip fas'
        },
        // Mail attachments have to be fetchable by the recipient (and by Meta),
        // so the composer uploads them public. Anything that is only ever read
        // back inside the admin — a ticket's own files — must not be, and gets
        // a signed url instead.
        visibility: {
            type: String,
            default: 'public'
        }
    },
    computed: {
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        }
    },
    methods: {
        endpoint() {
            let path = `${this.setupStore.uploadEndpoint}/${this.visibility}`
            if (this.activeCms) path += `/${this.activeCms.id}`
            return path
        },
        async upload({ file, onSuccess, onError }) {
            try {
                let token = AuthApi.getToken()
                try {
                    token = await ensureFreshToken()
                } catch (refreshError) {
                    // keep the existing token if refresh is unavailable
                }

                const formData = new FormData()
                formData.append('file', file)
                formData.append('context[type]', 'original')

                const response = await fetch(this.endpoint(), {
                    method: 'POST',
                    headers: { authorization: `Bearer ${token}`, accept: 'application/json' },
                    body: formData
                })

                if (!response.ok) {
                    throw new Error('Upload failed')
                }

                const data = await response.json()
                this.$emit('attached', { id: data.id, name: data.name, size: data.size, url: data.url })
                onSuccess(data)
            } catch (error) {
                onError(error)
                this.$notify.error({
                    title: this.$t('Upload failed'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                })
            }
        }
    }
}
</script>

<style lang="sass" scoped>
.attachment-upload
    display: inline-block
    // .plus-box carries its own metrics globally (globals.sass) — the tile
    // must look the same here as everywhere else.
    &.plus-box
        display: block
        width: 100%
</style>
